/**
 * "Services" listing page (/services) content — a singleton, editable the
 * same way as the About page: per-field text editing plus whole-section
 * show/hide. The "Core Capabilities" cards themselves are NOT stored here —
 * they're generated from `servicePages` (the "Service Pages" admin tab), one
 * card per created page. DEFAULT_PAGE_CONTENT below IS the page's original
 * hardcoded copy (Services.jsx hero + ServicesArchitecturePipeline.jsx), so
 * the CMS-driven page renders byte-identical to what shipped before.
 */
export const DEFAULT_PAGE_CONTENT = {
  hiddenSections: [],
  hero: {
    titleAccent: 'AI-Powered',
    titleRest: 'Solutions To Transform Your Business',
    lede:
      'Build secure, custom AI solutions that automate workflows, improve decision-making, and increase operational efficiency. From AI strategy to implementation, Databiqs delivers intelligent chatbots, AI automation, and machine-learning solutions designed for sustainable business growth.',
  },
  pipeline: {
    eyebrow: 'END-TO-END AI DEVELOPMENT PROCESS',
    titleLine: 'From AI Strategy to',
    titleAccent: 'Production-Ready Solutions',
    steps: [
      {
        title: 'Discover',
        description: 'Assess workflows, data readiness, and automation opportunities through a structured AI readiness assessment.',
      },
      {
        title: 'Design',
        description: 'Define your AI solution architecture, governance requirements, integrations, and milestones tied to measurable ROI.',
      },
      {
        title: 'Build',
        description: 'Develop production-ready AI models, workflow automations, system integrations, and human-in-the-loop controls.',
      },
      {
        title: 'Scale',
        description: 'Use MLOps, performance monitoring, and model-drift management to scale reliable AI systems across your organization.',
      },
    ],
  },
  coreCapabilities: {
    title: 'CORE CAPABILITIES',
    subtitle:
      'Specialized intelligence modules engineered for enterprise scale and precision. Each solution is tailored to your unique business needs.',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createServicesPageTemplate() {
  return deepClone(DEFAULT_PAGE_CONTENT);
}

const mergeArrayOfObjects = (storedArr, tplArr) => {
  if (!Array.isArray(storedArr) || !storedArr.length) return tplArr;
  return storedArr.map((item, i) => ({ ...(tplArr[i] || tplArr[0] || {}), ...(item || {}) }));
};

/** Deep-merges stored (possibly partial/older) page data over the current defaults so the renderer never crashes. */
export function normalizeServicesPageData(stored) {
  const tpl = createServicesPageTemplate();
  if (!stored || typeof stored !== 'object') return tpl;

  return {
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    hero: { ...tpl.hero, ...(stored.hero || {}) },
    pipeline: {
      ...tpl.pipeline,
      ...(stored.pipeline || {}),
      steps: mergeArrayOfObjects(stored.pipeline?.steps, tpl.pipeline.steps),
    },
    coreCapabilities: { ...tpl.coreCapabilities, ...(stored.coreCapabilities || {}) },
  };
}

export function createDefaultServicesPageStyles() {
  return {};
}

export function normalizeServicesPageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full normalized { data, styles } shape, the way it's stored under content.servicesPage. */
export function normalizeServicesPage(stored) {
  return {
    data: normalizeServicesPageData(stored?.data),
    styles: normalizeServicesPageStyles(stored?.styles),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
